import React from 'react';

import classNames from 'classnames';

import Badge from 'CommonComponents/Badge';

/**
 * Card chrome for one Actuals Dashboard tile.
 *
 * Matches SectionTile's surface (white, tw-rounded-lg, tw-shadow-primary) rather
 * than BaseTile's bordered box: these are panels of mixed content, not single
 * stat boxes, and they sit in a grid beside one another.
 *
 * `action` is the tile's click-through — every figure on this dashboard leads
 * somewhere you can act on it, so a tile without one is the exception.
 */
export default function ActualsDashboardTile({ title, action, children, className }) {
    return (
        <section className={classNames('tw-flex tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5', className)}>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-3">
                <h3 className="table__header">{title}</h3>
                {action}
            </div>
            <div className="tw-mt-4 tw-flex-auto">{children}</div>
        </section>
    );
}

/**
 * The "→" link in a tile header that drops the reader onto the tab holding the
 * rows behind the figure.
 */
export function TileAction({ label, onClick }) {
    return (
        <button type="button" onClick={onClick} className="tw-shrink-0 tw-text-xs tw-font-medium tw-text-blue hover:tw-underline">
            {label} <span aria-hidden="true">&rarr;</span>
        </button>
    );
}

/**
 * A tile whose data does not exist yet.
 *
 * Deliberately not hidden and not a broken-looking empty box: the reader is told
 * what the tile will show and that it is not ready, so an absent figure reads as
 * "not built yet" rather than "zero" or "failed to load".
 */
export function ComingSoonTile({ title, description, className }) {
    return (
        <ActualsDashboardTile
            title={title}
            action={
                <Badge variant="neutral" size="sm">
                    {$t('Coming soon')}
                </Badge>
            }
            className={className}
        >
            <div className="tw-flex tw-h-full tw-items-center tw-rounded-lg tw-bg-charcoal-10 tw-px-4 tw-py-6">
                <p className="tw-text-xs tw-text-charcoal-50">{description}</p>
            </div>
        </ActualsDashboardTile>
    );
}
